<template>
    <component :is="tag">
        <slot
            :is-loading="isLoading"
            :is-delivering="isDelivering"
            :deliver-task="deliverTask"
            :errors="errors"
            :error-message="errorMessage"
            :error-code="errorCode"
            :goto-task-step="gotoTaskStep"
            :task="task"
            :brand-networks="brandNetworks"
            :upload-content-url="uploadContentUrl"
            :delete-uploaded-content-url="deleteUploadedContentUrl"
            :uploaded-content-delivery-url="uploadedContentDeliveryUrl"
            :handle-file-change="handleFileChange"
            :handle-reset-uploads="handleResetUploads"
            :handle-upload-failed="handleUploadFailed"
            :upload-headers="uploadHeaders"
            :uploaded-files="uploadedFiles"
            :has-uploaded-files="hasUploadedFiles"
            :is-file-uploading="isFileUploading"
        />
    </component>
</template>

<script>
    import BaseProviderMixin from './mixins/BaseProviderMixin';
    import CampaignsMixin from './mixins/CampaignsMixin';
    import ProposalTasksMixin from './mixins/ProposalTasksMixin';
    import ContactsMixin from './mixins/ContactsMixin';
    import ProposalsMixin from './mixins/ProposalsMixin';
    import UploadMixin from './mixins/UploadMixin';
    import useBrands from '../../../src/composables/use-brands';

    export default {
        name: 'TaskDeliveryProvider',
        mixins: [BaseProviderMixin, ContactsMixin, CampaignsMixin, ProposalsMixin, ProposalTasksMixin, UploadMixin],
        data() {
            return {
                isDelivering: false,
                isFileUploading: false,
                isFileCanceling: false,
                errorCode: null,
                brandNetworks: null
            };
        },
        computed: {
            deliveryUrl() {
                return `/api/v1/campaigns/${this.campaignParamId}/jobs/${this.job?.id}/content`;
            },
            uploadedContentDeliveryUrl() {
                return `/api/v1/campaigns/${this.campaignParamId}/jobs/${this.job?.id}/uploaded-content`;
            },
            uploadContentUrl() {
                return `/contact/${this.contact?.id}/jobs/${this.job?.id}/uploaded-content/upload?network_id=${this.task?.network_id}`;
            },
            deleteUploadedContentUrl() {
                return `/contact/${this.contact?.id}/jobs/${this.job?.id}/uploaded-content/`;
            }
        },
        methods: {
            gotoTaskStep() {
                this.$router.push({ name: 'v2.campaign.proposal.tasks' });
            },
            deliverTask(url, postedAt) {
                this.isDelivering = true;
                this.resetErrors();

                this.$http
                    .post(this.deliveryUrl, {
                        url,
                        posted_at: postedAt
                    })
                    .then(() => {
                        this.gotoTaskStep();
                    })
                    .catch((error) => {
                        this.errorCode = error.status;

                        if (error.status === 404) {
                            this.errorMessage = 'Task could not be found.';
                            return;
                        }
                        if (error.status === 423) {
                            this.errorMessage = 'Thank you! This content has been collected.';
                            return;
                        }
                        this.handleHttpError(error);
                    })
                    .then(() => {
                        this.isDelivering = false;
                    });
            },
            handleFileChange(info) {
                if (this.isFileCanceling) {
                    this.isFileCanceling = false;
                    return;
                }

                this.isFileUploading = true;
                this.handleUploadChange(
                    info,
                    () => (this.isFileUploading = false),
                    () => {
                        this.handleUploadError(info);
                        this.isFileUploading = false;
                    },
                    true
                );
            },
            handleResetUploads() {
                this.handleResetUploadedFiles();
            },
            handleUploadFailed() {
                this.isFileUploading = false;
                this.isFileCanceling = true;
            },
            async loadBrandNetworks() {
                const response = await useBrands().getSocialAccounts(this.campaign?.brand?.id);
                this.brandNetworks = response.data;
            }
        },

        async created() {
            this.isLoading = true;

            try {
                await this.loadContact(true);
                await this.loadCampaign();
                await this.loadProposal();
                await this.loadBrandNetworks();
            } catch (error) {
                this.handleHttpError(error);
            }

            this.isLoading = false;
        }
    };
</script>
